import { useState, useCallback, useMemo, useRef } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useResultsPool } from './useResultsPool';

/**
 * Results Data Management Hook
 * 
 * Provides comprehensive results data management with:
 * - Pooled results loading
 * - Data parsing and normalization
 * - Content extraction (vehicles, people, text, etc.)
 * - Statistics and analytics
 * - Caching and performance optimization
 */

export const useResultsData = ({
  jobIds = [],
  autoLoad = true,
  parseContent = true
} = {}) => {
  
  // Use results pool for efficient data loading
  const { results: pooledResults } = useResultsPool(jobIds);
  
  // State for additional data and processing
  const [state, setState] = useState({
    loading: false,
    error: null,
    detailedResults: {},
    parsedContent: {},
    lastUpdated: null
  });

  const loadingJobsRef = useRef(new Set());

  // Parse structured data from results
  const parseStructuredData = useCallback((structuredData) => {
    if (!structuredData || !parseContent) return null;

    try {
      const parsed = {
        vehicles: [],
        people: [],
        licensePlates: [],
        activities: [],
        textItems: [],
        timeline: [],
        summary: null,
        confidence: null
      };

      // Parse vehicles
      if (structuredData.vehicles) {
        parsed.vehicles = structuredData.vehicles.map(vehicle => ({
          id: vehicle.id || `vehicle_${Math.random()}`,
          type: vehicle.type || vehicle.category,
          color: vehicle.color,
          make: vehicle.make,
          model: vehicle.model,
          confidence: vehicle.confidence,
          boundingBoxes: vehicle.bounding_boxes || [],
          timestamps: vehicle.timestamps || [],
          licensePlate: vehicle.license_plate
        }));
      }

      // Parse people
      if (structuredData.people) {
        parsed.people = structuredData.people.map(person => ({
          id: person.id || `person_${Math.random()}`,
          description: person.description,
          clothing: person.clothing,
          actions: person.actions || [],
          confidence: person.confidence,
          boundingBoxes: person.bounding_boxes || [],
          timestamps: person.timestamps || []
        }));
      }

      // Parse license plates
      if (structuredData.license_plates) {
        parsed.licensePlates = structuredData.license_plates.map(plate => ({
          id: plate.id || `plate_${Math.random()}`,
          text: plate.text || plate.number,
          confidence: plate.confidence,
          state: plate.state,
          country: plate.country,
          boundingBoxes: plate.bounding_boxes || [],
          timestamps: plate.timestamps || [],
          vehicleId: plate.vehicle_id
        }));
      }

      // Parse activities
      if (structuredData.activities) {
        parsed.activities = structuredData.activities.map(activity => ({
          id: activity.id || `activity_${Math.random()}`,
          type: activity.type,
          description: activity.description,
          confidence: activity.confidence,
          startTime: activity.start_time,
          endTime: activity.end_time,
          participants: activity.participants || []
        }));
      }

      // Parse text items
      if (structuredData.text_items || structuredData.text) {
        const textItems = structuredData.text_items || structuredData.text;
        parsed.textItems = textItems.map(item => ({
          id: item.id || `text_${Math.random()}`,
          text: item.text || item.content,
          confidence: item.confidence,
          boundingBoxes: item.bounding_boxes || [],
          timestamps: item.timestamps || [],
          category: item.category || 'text'
        }));
      }

      // Parse timeline
      if (structuredData.timeline) {
        parsed.timeline = structuredData.timeline.map(event => ({
          id: event.id || `event_${Math.random()}`,
          timestamp: event.timestamp || event.time,
          description: event.description,
          type: event.type,
          confidence: event.confidence,
          entities: event.entities || []
        })).sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
      }

      // Extract summary
      parsed.summary = structuredData.summary || null;
      parsed.confidence = structuredData.overall_confidence || 
                         structuredData.confidence || null;

      return parsed;
    } catch (error) {
      console.error('Error parsing structured data:', error);
      return null;
    }
  }, [parseContent]);

  // Load detailed results for a specific job
  const loadDetailedResults = useCallback(async (jobId) => {
    if (loadingJobsRef.current.has(jobId)) return;
    
    loadingJobsRef.current.add(jobId);
    setState(prev => ({ ...prev, loading: true }));

    try {
      const resultDoc = await getDoc(doc(db, 'analysisResults', jobId));
      
      if (resultDoc.exists()) {
        const resultData = resultDoc.data();
        const parsedContent = parseStructuredData(resultData.structuredData);
        
        setState(prev => ({
          ...prev,
          detailedResults: {
            ...prev.detailedResults,
            [jobId]: resultData
          },
          parsedContent: {
            ...prev.parsedContent,
            [jobId]: parsedContent
          },
          lastUpdated: Date.now()
        }));
        
        return { data: resultData, parsed: parsedContent };
      }
      
      return null;
    } catch (error) {
      console.error(`Error loading detailed results for job ${jobId}:`, error);
      setState(prev => ({
        ...prev,
        error: error.message
      }));
      throw error;
    } finally {
      loadingJobsRef.current.delete(jobId);
      setState(prev => ({ ...prev, loading: false }));
    }
  }, [parseStructuredData]);

  // Get results for specific job
  const getResultsForJob = useCallback((jobId) => {
    return {
      pooled: pooledResults[jobId] || null,
      detailed: state.detailedResults[jobId] || null,
      parsed: state.parsedContent[jobId] || null
    };
  }, [pooledResults, state.detailedResults, state.parsedContent]);

  // Get all vehicles across all results
  const getAllVehicles = useCallback(() => {
    const vehicles = [];
    Object.values(state.parsedContent).forEach(parsed => {
      if (parsed?.vehicles) {
        vehicles.push(...parsed.vehicles);
      }
    });
    return vehicles;
  }, [state.parsedContent]);

  // Get all people across all results
  const getAllPeople = useCallback(() => {
    const people = [];
    Object.values(state.parsedContent).forEach(parsed => {
      if (parsed?.people) {
        people.push(...parsed.people);
      }
    });
    return people;
  }, [state.parsedContent]);

  // Get all license plates across all results
  const getAllLicensePlates = useCallback(() => {
    const plates = [];
    Object.values(state.parsedContent).forEach(parsed => {
      if (parsed?.licensePlates) {
        plates.push(...parsed.licensePlates);
      }
    });
    return plates;
  }, [state.parsedContent]);

  // Get combined timeline
  const getCombinedTimeline = useCallback(() => {
    const events = [];
    Object.entries(state.parsedContent).forEach(([jobId, parsed]) => {
      if (parsed?.timeline) {
        events.push(...parsed.timeline.map(event => ({
          ...event,
          jobId,
          source: 'analysis'
        })));
      }
    });
    return events.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
  }, [state.parsedContent]);

  // Statistics across all results
  const stats = useMemo(() => {
    const allParsed = Object.values(state.parsedContent).filter(Boolean);
    const allPooled = Object.values(pooledResults).filter(Boolean);
    
    return {
      totalResults: allPooled.length,
      parsedResults: allParsed.length,
      totalVehicles: allParsed.reduce((sum, p) => sum + (p.vehicles?.length || 0), 0),
      totalPeople: allParsed.reduce((sum, p) => sum + (p.people?.length || 0), 0),
      totalLicensePlates: allParsed.reduce((sum, p) => sum + (p.licensePlates?.length || 0), 0),
      totalActivities: allParsed.reduce((sum, p) => sum + (p.activities?.length || 0), 0),
      totalTextItems: allParsed.reduce((sum, p) => sum + (p.textItems?.length || 0), 0),
      totalTimelineEvents: allParsed.reduce((sum, p) => sum + (p.timeline?.length || 0), 0),
      avgConfidence: allParsed.length > 0 ? 
        allParsed.reduce((sum, p) => sum + (p.confidence || 0), 0) / allParsed.length : 0,
      providersUsed: [...new Set(allPooled.map(r => r.serviceId).filter(Boolean))],
      modelsUsed: [...new Set(allPooled.map(r => r.modelName).filter(Boolean))]
    };
  }, [state.parsedContent, pooledResults]);

  // Content summary
  const contentSummary = useMemo(() => {
    const vehicles = getAllVehicles();
    const people = getAllPeople();
    const plates = getAllLicensePlates();
    
    return {
      vehicles: {
        total: vehicles.length,
        byType: vehicles.reduce((acc, v) => {
          const type = v.type || 'unknown';
          acc[type] = (acc[type] || 0) + 1;
          return acc;
        }, {}),
        byColor: vehicles.reduce((acc, v) => {
          const color = v.color || 'unknown';
          acc[color] = (acc[color] || 0) + 1;
          return acc;
        }, {}),
        withPlates: vehicles.filter(v => v.licensePlate).length
      },
      people: {
        total: people.length,
        withDescriptions: people.filter(p => p.description).length,
        byClothing: people.reduce((acc, p) => {
          if (p.clothing) {
            Object.keys(p.clothing).forEach(item => {
              acc[item] = (acc[item] || 0) + 1;
            });
          }
          return acc;
        }, {})
      },
      licensePlates: {
        total: plates.length,
        byState: plates.reduce((acc, p) => {
          const state = p.state || 'unknown';
          acc[state] = (acc[state] || 0) + 1;
          return acc;
        }, {}),
        unique: [...new Set(plates.map(p => p.text))].length
      }
    };
  }, [getAllVehicles, getAllPeople, getAllLicensePlates]);

  // Search across all content
  const searchContent = useCallback((searchTerm) => {
    const term = searchTerm.toLowerCase();
    const results = [];
    
    Object.entries(state.parsedContent).forEach(([jobId, parsed]) => {
      if (!parsed) return;
      
      // Search vehicles
      parsed.vehicles?.forEach(vehicle => {
        if (vehicle.type?.toLowerCase().includes(term) ||
            vehicle.color?.toLowerCase().includes(term) ||
            vehicle.make?.toLowerCase().includes(term) ||
            vehicle.model?.toLowerCase().includes(term)) {
          results.push({
            type: 'vehicle',
            jobId,
            item: vehicle,
            match: 'Vehicle information'
          });
        }
      });
      
      // Search people
      parsed.people?.forEach(person => {
        if (person.description?.toLowerCase().includes(term)) {
          results.push({
            type: 'person',
            jobId,
            item: person,
            match: 'Person description'
          });
        }
      });
      
      // Search license plates
      parsed.licensePlates?.forEach(plate => {
        if (plate.text?.toLowerCase().includes(term) ||
            plate.state?.toLowerCase().includes(term)) {
          results.push({
            type: 'license_plate',
            jobId,
            item: plate,
            match: 'License plate'
          });
        }
      });
      
      // Search text items
      parsed.textItems?.forEach(textItem => {
        if (textItem.text?.toLowerCase().includes(term)) {
          results.push({
            type: 'text',
            jobId,
            item: textItem,
            match: 'Detected text'
          });
        }
      });
    });
    
    return results;
  }, [state.parsedContent]);

  return {
    // Data
    pooledResults,
    detailedResults: state.detailedResults,
    parsedContent: state.parsedContent,
    stats,
    contentSummary,
    
    // State
    loading: state.loading,
    error: state.error,
    lastUpdated: state.lastUpdated,
    
    // Actions
    loadDetailedResults,
    
    // Utilities
    getResultsForJob,
    getAllVehicles,
    getAllPeople,
    getAllLicensePlates,
    getCombinedTimeline,
    searchContent,
    
    // Metadata
    hasResults: Object.keys(pooledResults).length > 0,
    jobIds: Object.keys(pooledResults)
  };
};
